import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { translateIn, type Lang } from "@aiw/i18n";
import { allCapturedTrims, effectiveTrimLengthMM } from "@artemis-group/aiw-contracts";
import { TrimCaptureEditor } from '../components/TrimCaptureEditor';
import {CanvasWallEdges,CanvasPhysicalWalls} from '../components/canvas-wall-edges';
import { api, authHeaders, client, fetchImpl } from "../lib/api";
import { env } from "../lib/env";
import { useT } from "../lib/i18n";
import type { HarnessDocument, HarnessRevision, HarnessUser, HarnessWork } from "../mock/work-record";
import {WorkTabs} from "../components/work-tabs";

const COPY = {
  en: {
    title: "Site Canvas",
    kicker: "Shared work record · Opportunity → Job → Installation",
    lede: "Review and update the site plan and its physical details. Every stage opens this same record; Proposal and Installation can pin an explicit saved revision.",
    disabled: "This test harness is available only in the local mock workspace.",
    reset: "Reset test case",
    stage: "Advance to Job",
    user: "Acting as",
    filter: "Revision filter",
    all: "All users",
    saved: "Saved",
    dirty: "Unsaved changes",
    save: "Save revision",
    leave: "Try to exit",
    room: "Living + Hall",
    instruction: "The shape stays as captured. Enter verified wall-face dimensions; the engine will propose an as-is → to-be rebuild for approval before geometry changes.",
    note: "Site note",
    measurement: "Wall-face measurement (mm)",
    trimLength: "Doorway trim (mm)",
    trimRuns: "Trim runs and purchase pieces",
    addTrim: "Add trim run",
    removeTrim: "Delete",
    runLabel: "Run",
    productCode: "Product / colour code",
    stockLength: "Stock piece (mm)",
    breakKind: "Join condition",
    straight: "Visible straight",
    natural: "Doorway / 45° mitre",
    purchaseSummary: "Purchase summary",
    pieces: "pieces",
    offcut: "left",
    orderReady: "Measured and ready to order",
    orderBlocked: "Check before ordering",
    access: "Site access",
    skirting: "Skirting",
    scotia: "Scotia",
    canvasHint: "As captured · reference shape",
    history: "Saved revisions",
    use: "Use as my working copy",
    source: "Copied from {name} revision {number}",
    noSource: "Own working copy",
    exitTitle: "You have unsaved changes",
    exitBody: "Save a new revision under your user ID before leaving?",
    saveExit: "Save & Exit",
    discardExit: "Exit Without Saving",
    cancel: "Cancel",
    exitedSaved: "Exited after saving.",
    exitedDiscarded: "Exited without saving. The saved revision is unchanged.",
    cleanExit: "Exited. There were no unsaved changes.",
    opportunity: "Opportunity",
    job: "Job",
    expectation: "Expected now",
    expectationText: "Save four times as one person: every authored revision remains immutable. Another person's history stays untouched.",
    details: "What this proves",
    detailsText: "AIW edits the same AssessmentV15 object Mobile writes. Stable IDs, geometry, anchored measurement, doorway, trim, registers, assumptions and site details survive the return trip.",
    revision: "Revision",
    assumed: "assumed",
    mobileSurface: "Mobile",
    aiwSurface: "AIW web",
    installerSurface: "Installer PWA",
    marker: "As-captured canvas geometry",
    floor: "Floor",
    area: "Area",
    physicalWalls: "Physical walls",
    readiness: "Proposal readiness",
    ready: "Confirmed",
    missing: "Missing",
    planCheck: "Closed floor area",
    wallsCheck: "Physical wall lines",
    measureCheck: "Verified measurement",
    finishesCheck: "Existing floor, wall and edge details",
    accessCheck: "Site access details",
  },
  zh: {
    title: "现场画布",
    kicker: "共享工作记录 · 商机 → 工程 → 安装",
    lede: "核对并更新现场平面图及实体资料。各阶段打开的是同一份记录；方案与安装可明确锁定某个已保存版本。",
    disabled: "此测试工具只在本地模拟工作区可用。",
    reset: "重置测试案例",
    stage: "推进为工程",
    user: "当前用户",
    filter: "版本筛选",
    all: "全部用户",
    saved: "已保存",
    dirty: "有未保存更改",
    save: "保存新版本",
    leave: "尝试退出",
    room: "客厅 + 走廊",
    instruction: "图形保持现场记录状态。输入核实后的墙面尺寸；系统先提出原图 → 重建图，批准后才更改几何。",
    note: "现场备注",
    measurement: "墙面到墙面测量（毫米）",
    trimLength: "门口收口条（毫米）",
    trimRuns: "收口条长度和采购支数",
    addTrim: "新增收口条",
    removeTrim: "删除",
    runLabel: "位置",
    productCode: "产品 / 颜色编号",
    stockLength: "每支长度（毫米）",
    breakKind: "接驳位置",
    straight: "可见直线",
    natural: "门口 / 45° 转角",
    purchaseSummary: "采购计算",
    pieces: "支",
    offcut: "剩余",
    orderReady: "尺寸齐全，可以订购",
    orderBlocked: "订购前需要检查",
    access: "现场出入条件",
    skirting: "踢脚线",
    scotia: "Scotia 收边条",
    canvasHint: "现场原图 · 参考形状",
    history: "已保存版本",
    use: "作为我的工作副本",
    source: "复制自 {name} 的第 {number} 版",
    noSource: "自己的工作副本",
    exitTitle: "你有未保存的更改",
    exitBody: "退出前，要以你的用户身份保存一个新版本吗？",
    saveExit: "保存并退出",
    discardExit: "不保存并退出",
    cancel: "取消",
    exitedSaved: "已保存并退出。",
    exitedDiscarded: "已退出且未保存。原保存版本没有变化。",
    cleanExit: "已退出。没有未保存更改。",
    opportunity: "商机",
    job: "工程",
    expectation: "现在请检查",
    expectationText: "同一用户保存四次后，每个已保存版本都保持不可修改；其他用户的历史不受影响。",
    details: "此测试证明什么",
    detailsText: "AIW 编辑移动端写入的同一 AssessmentV15 对象。稳定 ID、几何、定位测量线、门口、收口条、现场清单、假设和现场资料在返回移动端后保持完整。",
    revision: "版本",
    assumed: "假设值",
    mobileSurface: "移动端",
    aiwSurface: "AIW 网页",
    installerSurface: "安装工 PWA",
    marker: "现场原始画布几何",
    floor: "楼层",
    area: "区域",
    physicalWalls: "实体墙",
    readiness: "方案资料完整度",
    ready: "已确认",
    missing: "缺少",
    planCheck: "封闭地面区域",
    wallsCheck: "实体墙线",
    measureCheck: "已核实尺寸",
    finishesCheck: "原地面、墙体和收边资料",
    accessCheck: "现场出入资料",
  },
} as const;
type CopyId = keyof typeof COPY.en;

type Snapshot = { work: HarnessWork; revisions: HarnessRevision[]; users: HarnessUser[] };
const formatSydney = (value: string, lang: Lang) => new Intl.DateTimeFormat(lang === "zh" ? "zh-CN" : "en-AU", {
  timeZone: "Australia/Sydney", dateStyle: "medium", timeStyle: "short",
}).format(new Date(value));

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const live = !env.mock;
  const response = await fetchImpl((live ? env.apiBase : "") + path, {
    ...init,
    headers: { ...(live ? await authHeaders() : {}), ...(init?.headers ?? {}) },
  });
  const body = await response.json() as { ok?: boolean; data?: T; error?: string } & T;
  if (!response.ok || body.ok === false) throw new Error(body.error ?? "Request failed");
  return (body.data ?? body) as T;
}

export function WorkRecordReview() {
  const { ref } = useParams();
  const { lang } = useT();
  const workCopy = (id: CopyId, vars?: Record<string, string | number>) => translateIn(COPY, lang as Lang, id, vars);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [userId, setUserId] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState("");
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [draft, setDraft] = useState<HarnessDocument | null>(null);
  const [dirty, setDirty] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [floorId, setFloorId] = useState("");
  const [areaId, setAreaId] = useState("");
  const [trimEditing, setTrimEditing] = useState(false);

  const load = async (preferredRevision?: string) => {
    setLoadError("");
    let next: Snapshot;
    let bootstrap: HarnessDocument | null = null;
    let liveActorID: string | null = null;
    if (env.mock) next = await request<Snapshot>("/api/mock/work-record");
    else {
      if (!ref) throw new Error("Work reference is missing");
      const work = await request<HarnessWork>(`/api/work-records/by-reference/${encodeURIComponent(ref)}`);
      const [revisions, me] = await Promise.all([
        request<HarnessRevision[]>(`/api/work-records/${encodeURIComponent(work.id)}/revisions`),
        request<{user:{id:string;email?:string};actor:{id:string}|null}>("/api/me"),
      ]);
      if (!revisions.length) bootstrap = await request<HarnessDocument>(`/api/work-records/by-reference/${encodeURIComponent(ref)}/bootstrap-source`).catch(() => null);
      if (!revisions.length && !bootstrap) throw new Error("No mobile assessment or saved site-record revision is available yet.");
      const people = new Map<string,HarnessUser>();
      revisions.forEach(row => people.set(row.owner_user_id,{id:row.owner_user_id,name:row.owner_name,device:row.device_name,surface:row.surface}));
      const actorID=me.actor?.id??me.user.id;
      liveActorID=actorID;
      if(!people.has(actorID)) people.set(actorID,{id:actorID,name:me.user.email?.split('@')[0]??'Current user',device:'AIW browser',surface:'aiw'});
      next={work,revisions,users:[...people.values()]};
    }
    setSnapshot(next);
    const acting = liveActorID ?? (userId || next.users[0]!.id);
    setUserId(acting);
    const chosen = next.revisions.find((row) => row.id === preferredRevision)
      ?? next.revisions.find((row) => row.owner_user_id === acting)
      ?? next.revisions[0];
    const document = structuredClone(chosen?.document ?? bootstrap);
    setSelectedId(chosen?.id ?? "");
    setDraft(document);
    setFloorId(document?.assessment.floors[0]?.id ?? "");
    setAreaId(document?.assessment.floors[0]?.areas[0]?.id ?? "");
    setSourceId(chosen && chosen.owner_user_id !== acting ? chosen.id : null);
    setDirty(!chosen && !!bootstrap);
  };

  useEffect(() => { void load().catch(error=>setLoadError((error as Error).message)); }, [ref]);
  useEffect(() => { setStatus(""); }, [lang]);

  const actingUser = snapshot?.users.find((user) => user.id === userId) ?? null;
  const revisions = useMemo(() => {
    if (!snapshot) return [];
    return filter === "all" ? snapshot.revisions : snapshot.revisions.filter((row) => row.owner_user_id === filter);
  }, [snapshot, filter]);
  const selected = snapshot?.revisions.find((row) => row.id === selectedId) ?? null;

  const choose = (revision: HarnessRevision) => {
    if(trimEditing){setStatus(lang==='zh'?'请先确认或取消收口条编辑。':'Confirm or cancel the trim editor first.');return;}
    setSelectedId(revision.id);
    setDraft(structuredClone(revision.document));
    setFloorId(revision.document.assessment.floors[0]?.id ?? "");
    setAreaId(revision.document.assessment.floors[0]?.areas[0]?.id ?? "");
    setSourceId(revision.owner_user_id === userId ? null : revision.id);
    setDirty(false);
    setStatus("");
  };
  const useRevision = (revision: HarnessRevision) => {
    if(trimEditing){setStatus(lang==='zh'?'请先确认或取消收口条编辑。':'Confirm or cancel the trim editor first.');return;}
    setSelectedId(revision.id);
    setDraft(structuredClone(revision.document));
    setFloorId(revision.document.assessment.floors[0]?.id ?? "");
    setAreaId(revision.document.assessment.floors[0]?.areas[0]?.id ?? "");
    setSourceId(revision.id);
    setDirty(true);
    setStatus(workCopy("source", { name: revision.owner_name, number: revision.save_number }));
  };
  const save = async (): Promise<void> => {
    if (!snapshot || !draft || !actingUser) return;
    if (trimEditing) { setStatus(lang==='zh'?'请先确认或取消收口条编辑。':'Confirm or cancel the trim editor first.'); return; }
    setBusy(true);
    try {
      const latestOwn = snapshot.revisions.filter((row) => row.owner_user_id === actingUser.id).sort((a,b) => b.save_number-a.save_number)[0] ?? null;
      const args = {
          expected_latest_revision_id: latestOwn?.id ?? null,
          source_revision_id: sourceId,
          device:{id:`aiw-${navigator.userAgent.slice(0,80)}`,name:'AIW browser',surface:'aiw' as const},
          idempotency_key: crypto.randomUUID(),
          document: draft,
      };
      let savedID:string,savedAt:string;
      if(env.mock){
        const saved=await request<HarnessRevision>("/api/mock/work-record/save",{method:"POST",headers:{"content-type":"application/json"},body:JSON.stringify({...args,owner_user_id:actingUser.id})});
        savedID=saved.id;savedAt=saved.saved_at;
      }else{
        const result=await api.propose(client,{command:'save_work_revision',args:{...args,work_id:snapshot.work.id},idempotency_key:args.idempotency_key});
        if(result.status!=='executed')throw new Error(result.status==='pending'?'Save is awaiting approval.':result.reason);
        const saved=result.result as {id:string;saved_at:string};savedID=saved.id;savedAt=saved.saved_at;
      }
      await load(savedID);
      setStatus(`${workCopy("saved")} · ${actingUser.name} · ${formatSydney(savedAt, lang as Lang)}`);
    } catch (error) { setStatus((error as Error).message); throw error; }
    finally { setBusy(false); }
  };
  const exit = () => {
    if (dirty) setExitOpen(true);
    else setStatus(workCopy("cleanExit"));
  };
  const saveAndExit = async () => {
    await save(); setExitOpen(false); setStatus(workCopy("exitedSaved"));
  };
  const discardAndExit = () => {
    if (selected) setDraft(structuredClone(selected.document));
    setDirty(false); setExitOpen(false); setStatus(workCopy("exitedDiscarded"));
  };
  const updateDraft = (change: (next: HarnessDocument) => void) => {
    if (!draft) return;
    const next = structuredClone(draft);
    change(next);
    setDraft(next);
    setDirty(true); setStatus("");
  };
  if(loadError)return <div className="page py-8"><p role="alert">{loadError}</p></div>;
  if (!snapshot || !draft || !actingUser) return <div className="page py-8">Loading…</div>;
  const floorIndex = Math.max(0, draft.assessment.floors.findIndex((candidate) => candidate.id === floorId));
  const floor = draft.assessment.floors[floorIndex];
  if (!floor) return <div className="page py-8"><p role="alert">No floor plan is available in this work record.</p></div>;
  const areaIndex = Math.max(0, floor.areas.findIndex((candidate) => candidate.id === areaId));
  const area = floor.areas[areaIndex];
  if (!area) return <div className="page py-8">
    <h1>{workCopy('title')}</h1>
    <select aria-label={workCopy('floor')} disabled={trimEditing} value={floor.id} onChange={e=>setFloorId(e.target.value)}>{draft.assessment.floors.map(f=><option key={f.id} value={f.id}>{f.name}</option>)}</select>
    <TrimCaptureEditor key={floor.id} floor={floor} lang={lang} onPendingChange={setTrimEditing} onChange={updated=>updateDraft(next=>{next.assessment.floors[floorIndex]=updated;next.quantities.trim_length_mm=next.assessment.floors.reduce((n,f)=>n+allCapturedTrims(f).reduce((m,t)=>m+effectiveTrimLengthMM(t,f),0),0);})}/>
    <button disabled={busy||!dirty||trimEditing} onClick={()=>void save().catch(()=>{})}>{workCopy('save')}</button>
    {status&&<p role="status">{status}</p>}
  </div>;
  const measure = area.measures[0];
  const trim = area.trims[0];
  const access = (draft.site_details.access as { value?: string } | undefined)?.value ?? "";
  const polygon = area.boundary.map((point) => `${point.x},${point.y}`).join(" ");
  const trimPoints = trim?.polyline?.map((point) => `${point.x},${point.y}`).join(" ") ?? "";
  const walls = floor.walls ?? [];
  const planPoints=[...area.boundary,...walls.flatMap(w=>[w.a,w.b])];
  const planMinX=Math.min(...planPoints.map(p=>p.x))-30,planMinY=Math.min(...planPoints.map(p=>p.y))-30;
  const planWidth=Math.max(100,Math.max(...planPoints.map(p=>p.x))-planMinX+30),planHeight=Math.max(100,Math.max(...planPoints.map(p=>p.y))-planMinY+30);
  const skirting = floor.registers.find((row) => row.kind === "skirting")?.value ?? "";
  const scotia = floor.registers.find((row) => row.kind === "scotia")?.value ?? "";
  const scotiaChoice = scotia === "none" ? "none" : "scotia";
  const skirtingChoice = skirting === "square set" ? "none" : skirting;
  const readiness = [
    { label: workCopy("planCheck"), complete: area.boundary.length >= 3 },
    { label: workCopy("wallsCheck"), complete: walls.length > 0 },
    { label: workCopy("measureCheck"), complete: area.measures.some((item) => Number(item.lengthMM) > 0) },
    { label: workCopy("finishesCheck"), complete: ["existingFloor", "wallType", "skirting", "scotia"].every((kind) => floor.registers.some((item) => item.kind === kind && item.value.trim())) },
    { label: workCopy("accessCheck"), complete: access.trim().length > 0 },
  ];

  const selectedArea = (next: HarnessDocument) => {
    const nextFloor = next.assessment.floors.find((candidate) => candidate.id === floor.id);
    const nextArea = nextFloor?.areas.find((candidate) => candidate.id === area.id);
    if (!nextFloor || !nextArea) throw new Error("Selected floor area no longer exists");
    return { floor: nextFloor, area: nextArea };
  };

  return (
    <div className="page py-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="kicker">{workCopy("kicker")}</p>
          <h1 className="mt-1 text-2xl font-semibold">{workCopy("title")}</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{workCopy("lede")}</p>
        </div>
        {env.mock&&<button className="rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted" onClick={async()=>{setBusy(true);await request("/api/mock/work-record/reset",{method:"POST"});await load();setStatus("");setBusy(false);}}>{workCopy("reset")}</button>}
      </div>

      <WorkTabs
        active="canvas"
        overviewHref={snapshot.work.stage==="job"&&snapshot.work.job_ref?`/jobs/${encodeURIComponent(snapshot.work.job_ref)}`:`/opportunities/${encodeURIComponent(snapshot.work.opportunity_ref)}`}
        canvasHref={`/work-record/${encodeURIComponent(snapshot.work.job_ref??snapshot.work.opportunity_ref)}`}
        proposalHref={snapshot.work.stage==="job"&&snapshot.work.job_ref?`/proposals/new?job_ref=${encodeURIComponent(snapshot.work.job_ref)}`:`/opportunities/${encodeURIComponent(snapshot.work.opportunity_ref)}?tab=proposal`}
        installationHref={snapshot.work.stage==="job"&&snapshot.work.job_ref?`/jobs/${encodeURIComponent(snapshot.work.job_ref)}#installation`:undefined}
        documentsHref={snapshot.work.stage==="job"&&snapshot.work.job_ref?`/archive?job=${encodeURIComponent(snapshot.work.job_ref)}`:undefined}
      />

      <section className="mb-4 grid gap-3 rounded-xl border bg-card p-4 shadow-sm md:grid-cols-[1fr_auto_auto]">
        <div>
          <div className="flex items-center gap-2"><span className="rounded-full bg-teal-100 px-2 py-1 text-xs font-semibold text-teal-900">{snapshot.work.stage === "job" ? workCopy("job") : workCopy("opportunity")}</span><strong>{snapshot.work.title}</strong></div>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{snapshot.work.id} · {snapshot.work.opportunity_ref}{snapshot.work.job_ref ? ` · ${snapshot.work.job_ref}` : ""}</p>
        </div>
        <label className="text-xs font-medium">{workCopy("user")}<select disabled={!env.mock} className="mt-1 block rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-70" value={userId} onChange={(e)=>{setUserId(e.target.value);setSourceId(selected?.owner_user_id===e.target.value?null:selected?.id??null);}}>{snapshot.users.map((user)=><option key={user.id} value={user.id}>{user.name} · {user.device}</option>)}</select></label>
        {env.mock&&<button disabled={busy||snapshot.work.stage==="job"} className="self-end rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40" onClick={async()=>{setBusy(true);const work=await request<HarnessWork>("/api/mock/work-record/stage",{method:"POST"});setSnapshot({...snapshot,work});setBusy(false);}}>{workCopy("stage")}</button>}
      </section>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_390px]">
        <section className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div><h2 className="font-semibold">{area.name}</h2><p className="text-xs text-muted-foreground">{workCopy("instruction")}</p></div>
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${dirty?"bg-amber-100 text-amber-900":"bg-emerald-100 text-emerald-900"}`}>{dirty?workCopy("dirty"):workCopy("saved")}</span>
          </div>
          <div className="mb-3 grid gap-2 sm:grid-cols-2">
            <label className="text-xs font-medium">{workCopy("floor")}<select disabled={trimEditing} className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm" value={floor.id} onChange={(event)=>{const nextFloor=draft.assessment.floors.find((candidate)=>candidate.id===event.target.value);setFloorId(event.target.value);setAreaId(nextFloor?.areas[0]?.id??"");}}>{draft.assessment.floors.map((candidate)=><option key={candidate.id} value={candidate.id}>{candidate.name}</option>)}</select></label>
            <label className="text-xs font-medium">{workCopy("area")}<select className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm" value={area.id} onChange={(event)=>setAreaId(event.target.value)}>{floor.areas.map((candidate)=><option key={candidate.id} value={candidate.id}>{candidate.name}</option>)}</select></label>
          </div>
          <div role="img" aria-label={workCopy("room")} className="h-[360px] overflow-hidden rounded-xl border-2 border-slate-700 bg-slate-50">
            <svg viewBox={`${planMinX} ${planMinY} ${planWidth} ${planHeight}`} className="h-full w-full" aria-label={workCopy("marker")}>
              <defs><pattern id="work-grid" width="24" height="24" patternUnits="userSpaceOnUse"><path d="M 24 0 L 0 0 0 24" fill="none" stroke="#dbe3e8" strokeWidth="1"/></pattern></defs>
              <rect x={planMinX} y={planMinY} width={planWidth} height={planHeight} fill="url(#work-grid)"/>
              <polygon points={polygon} fill="#dff5ef" stroke="none"/>
              <CanvasPhysicalWalls floor={floor}/>
              <CanvasWallEdges floor={floor} areas={[area]}/>
              {measure?.a&&measure.b&&<line x1={measure.a.x} y1={measure.a.y} x2={measure.b.x} y2={measure.b.y} stroke="#2563eb" strokeWidth="3" strokeDasharray="8 5"/>}
              {trimPoints&&<polyline points={trimPoints} fill="none" stroke="#d97706" strokeWidth="8" strokeLinecap="round"/>}
              {area.boundary.map((point,index)=><circle key={index} cx={point.x} cy={point.y} r="6" fill="#475569"/>) }
              <text x="82" y="90" fill="#334155" fontSize="18" fontWeight="600">{area.name}</text>
              <text x="82" y="116" fill="#475569" fontSize="14">{measure?.lengthMM?.toLocaleString() ?? "—"} mm · {area.finish as string}</text>
              <text x="82" y="142" fill="#0f766e" fontSize="14" fontWeight="600">{workCopy("canvasHint")}</text>
              <text x="82" y="166" fill="#0f172a" fontSize="13">{workCopy("physicalWalls")}: {walls.length}</text>
              {trim&&<text x="82" y="292" fill="#92400e" fontSize="14">{trim.type} · {trim.lengthMM} mm</text>}
            </svg>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {measure&&<label className="text-sm font-medium">{workCopy("measurement")}<input className="mt-1 w-full rounded-md border bg-background px-3 py-2" type="number" min="1" value={measure.lengthMM??""} onChange={(e)=>updateDraft((next)=>{const target=selectedArea(next).area.measures.find((item)=>item.id===measure.id);if(target)target.lengthMM=Number(e.target.value);})}/></label>}
            <label className="text-sm font-medium">{workCopy("skirting")}<select className="mt-1 w-full rounded-md border bg-background px-3 py-2" value={skirtingChoice} onChange={(e)=>updateDraft((next)=>{const row=selectedArea(next).floor.registers.find((item)=>item.kind==="skirting");if(row)row.value=e.target.value;})}><option value="existing skirting">Keep existing</option><option value="new skirting">New skirting</option><option value="none">No skirting</option></select></label>
            <label className="text-sm font-medium">{workCopy("scotia")}<select className="mt-1 w-full rounded-md border bg-background px-3 py-2" value={scotiaChoice} onChange={(e)=>updateDraft((next)=>{const row=selectedArea(next).floor.registers.find((item)=>item.kind==="scotia");if(row)row.value=e.target.value;})}><option value="scotia">Yes</option><option value="none">No</option></select></label>
          </div>
          <section className="mt-4 rounded-xl border bg-slate-50 p-3">
            <h3 className="font-semibold">{workCopy("readiness")}</h3>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {readiness.map((item)=><div key={item.label} className="flex items-center justify-between gap-3 rounded-lg border bg-white px-3 py-2 text-sm"><span>{item.label}</span><strong className={item.complete?"text-emerald-700":"text-amber-700"}>{item.complete?workCopy("ready"):workCopy("missing")}</strong></div>)}
            </div>
          </section>
          <TrimCaptureEditor key={floor.id} floor={floor} lang={lang} onPendingChange={setTrimEditing} onChange={(updated)=>updateDraft(next=>{const i=next.assessment.floors.findIndex(f=>f.id===floor.id);if(i>=0)next.assessment.floors[i]=updated;next.quantities.trim_length_mm=next.assessment.floors.reduce((sum,f)=>sum+allCapturedTrims(f).reduce((n,t)=>n+effectiveTrimLengthMM(t,f),0),0);})}/>
          <label className="mt-4 block text-sm font-medium">{workCopy("access")}<input className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm" value={access} onChange={(e)=>updateDraft((next)=>{next.site_details.access={value:e.target.value,status:"confirmed_later"};})}/></label>
          <label className="mt-4 block text-sm font-medium">{workCopy("note")}<textarea className="mt-1 min-h-24 w-full rounded-md border bg-background p-3 text-sm" value={draft.assessment.siteNote} onChange={(e)=>updateDraft((next)=>{next.assessment.siteNote=e.target.value;})}/></label>
          <div className="mt-4 flex flex-wrap gap-2">
            <button disabled={busy||!dirty||trimEditing} className="rounded-md bg-teal-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40" onClick={()=>void save().catch(()=>{})}>{workCopy("save")}</button>
            <button disabled={busy} className="rounded-md border px-4 py-2 text-sm font-semibold" onClick={exit}>{workCopy("leave")}</button>
            {status && <span role="status" className="self-center text-sm text-muted-foreground">{status}</span>}
          </div>
          <details className="mt-4 rounded-lg bg-muted/60 p-3 text-sm"><summary className="cursor-pointer font-semibold">{workCopy("details")}</summary><p className="mt-2 text-muted-foreground">{workCopy("detailsText")}</p></details>
        </section>

        <aside className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="mb-3 flex items-end justify-between gap-2"><h2 className="font-semibold">{workCopy("history")}</h2><label className="text-xs">{workCopy("filter")}<select className="ml-2 rounded border bg-background px-2 py-1" value={filter} onChange={(e)=>setFilter(e.target.value)}><option value="all">{workCopy("all")}</option>{snapshot.users.map((user)=><option key={user.id} value={user.id}>{user.name}</option>)}</select></label></div>
          <div className="space-y-2">
            {revisions.map((revision)=><article key={revision.id} className={`rounded-lg border p-3 ${selectedId===revision.id?"border-teal-600 bg-teal-50/60":"bg-background"}`}>
              <button className="w-full text-left" onClick={()=>choose(revision)}>
                <div className="flex justify-between gap-2"><strong>{revision.owner_name} · {workCopy("revision")} {revision.save_number}</strong><span className="text-xs text-muted-foreground">{workCopy(revision.surface === "mobile" ? "mobileSurface" : revision.surface === "aiw" ? "aiwSurface" : "installerSurface")}</span></div>
                <p className="mt-1 text-xs text-muted-foreground">{formatSydney(revision.saved_at, lang as Lang)} · {revision.device_name}</p>
                {revision.source_owner_name&&<p className="mt-1 text-xs text-violet-700">{workCopy("source",{name:revision.source_owner_name,number:revision.source_save_number??"?"})}</p>}
              </button>
              <button className="mt-2 text-xs font-semibold text-teal-800 underline" onClick={()=>useRevision(revision)}>{workCopy("use")}</button>
            </article>)}
          </div>
          {env.mock&&<div className="mt-4 rounded-lg border border-dashed p-3 text-xs"><strong>{workCopy("expectation")}</strong><p className="mt-1 text-muted-foreground">{workCopy("expectationText")}</p></div>}
        </aside>
      </div>

      {exitOpen&&<div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/50 p-4" role="dialog" aria-modal="true" aria-labelledby="exit-title">
        <div className="w-full max-w-md rounded-xl bg-card p-5 shadow-2xl"><h2 id="exit-title" className="text-lg font-semibold">{workCopy("exitTitle")}</h2><p className="mt-2 text-sm text-muted-foreground">{workCopy("exitBody")}</p><div className="mt-5 grid gap-2"><button className="rounded-md bg-teal-700 px-4 py-2 font-semibold text-white" onClick={()=>void saveAndExit()}>{workCopy("saveExit")}</button><button className="rounded-md border px-4 py-2 font-semibold" onClick={discardAndExit}>{workCopy("discardExit")}</button><button className="rounded-md px-4 py-2 text-sm" onClick={()=>setExitOpen(false)}>{workCopy("cancel")}</button></div></div>
      </div>}
    </div>
  );
}
